import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, PencilSimple, Trash, Ticket } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  code: '',
  description: '',
  discount_type: 'percent',
  discount_value: 10,
  package_ids: [],
  max_uses: '',
  expires_at: '',
  is_active: true,
};

const CouponsAdminPanel = () => {
  const { t } = useLanguage();
  const { getAuthHeaders } = useAuth();
  const [coupons, setCoupons] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, pRes] = await Promise.all([
        axios.get(`${API_URL}/coupons/admin`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/packages`),
      ]);
      setCoupons(cRes.data);
      setPackages(pRes.data);
    } catch (err) {
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({
      code: c.code,
      description: c.description || '',
      discount_type: c.discount_type,
      discount_value: c.discount_value,
      package_ids: c.package_ids || [],
      max_uses: c.max_uses ?? '',
      expires_at: c.expires_at ? c.expires_at.slice(0, 16) : '',
      is_active: c.is_active,
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/coupons/admin/${deleteTarget}`, { headers: getAuthHeaders() });
      toast.success(t('admin.coupons.deleted'));
      fetchAll();
    } catch (err) {
      toast.error('Delete failed');
    } finally { setDeleteTarget(null); }
  };

  const handleSave = async () => {
    if (!form.code || form.code.length < 2) {
      toast.error(t('admin.coupons.code_required'));
      return;
    }
    if (form.discount_type === 'percent' && (form.discount_value < 0 || form.discount_value > 100)) {
      toast.error(t('admin.coupons.percent_range'));
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      max_uses: form.max_uses === '' ? null : Number(form.max_uses),
      expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      package_ids: form.package_ids.length ? form.package_ids : null,
      discount_value: Number(form.discount_value) || 0,
    };
    try {
      if (editingId) {
        const { code: _omit, ...updatePayload } = payload;
        await axios.put(`${API_URL}/coupons/admin/${editingId}`, updatePayload, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API_URL}/coupons/admin`, payload, { headers: getAuthHeaders() });
      }
      toast.success(t('admin.coupons.saved'));
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const togglePackage = (pid) => {
    setForm((f) => ({
      ...f,
      package_ids: f.package_ids.includes(pid)
        ? f.package_ids.filter((x) => x !== pid)
        : [...f.package_ids, pid],
    }));
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const formatDiscount = (c) => {
    if (c.discount_type === 'free') return t('admin.coupons.free');
    if (c.discount_type === 'percent') return `${c.discount_value}%`;
    return `$${c.discount_value}`;
  };

  if (loading) return <div className="p-8 text-center text-[#52525B]">{t('common.loading')}</div>;

  return (
    <div className="bg-white border border-[#E4E4E7]" data-testid="coupons-admin-panel">
      <div className="flex items-center justify-between p-4 border-b border-[#E4E4E7]">
        <h3 className="text-lg font-semibold text-[#09090B] flex items-center gap-2">
          <Ticket size={20} className="text-[#0F1B36]" />
          {t('admin.coupons')} ({coupons.length})
        </h3>
        <Button
          onClick={openCreate}
          className="bg-[#0F1B36] hover:bg-[#0F1B36]/90 text-white rounded-none"
          data-testid="coupons-new-button"
        >
          <Plus size={16} className="mr-2" />
          {t('admin.coupons.new')}
        </Button>
      </div>

      {coupons.length === 0 ? (
        <div className="p-8 text-center text-[#52525B]" data-testid="coupons-empty">
          {t('admin.coupons.empty')}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.coupons.code')}</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.coupons.discount')}</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.coupons.packages')}</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.coupons.usage')}</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.coupons.expires')}</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-[#52525B]">{t('admin.coupons.status')}</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-wider text-[#52525B]">{t('admin.coupons.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {coupons.map((c) => (
              <TableRow key={c.id} data-testid={`coupon-row-${c.id}`}>
                <TableCell className="font-mono font-semibold">{c.code}</TableCell>
                <TableCell>
                  <span className={`text-xs px-2 py-1 ${c.discount_type === 'free' ? 'bg-[#16A34A]/10 text-[#16A34A]' : 'bg-[#0F1B36]/10 text-[#0F1B36]'}`}>
                    {formatDiscount(c)}
                  </span>
                </TableCell>
                <TableCell className="text-sm">
                  {!c.package_ids || c.package_ids.length === 0
                    ? t('admin.coupons.all_packages')
                    : c.package_ids.map((pid) => packages.find((p) => p.id === pid)?.name || pid).join(', ')}
                </TableCell>
                <TableCell className="text-sm font-mono">
                  {c.used_count}{c.max_uses !== null ? `/${c.max_uses}` : ` / ∞`}
                </TableCell>
                <TableCell className="text-sm text-[#52525B]">
                  {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell>
                  {c.is_active ? (
                    <span className="text-xs bg-[#16A34A]/10 text-[#16A34A] px-2 py-1">{t('admin.coupons.active')}</span>
                  ) : (
                    <span className="text-xs bg-[#F4F4F5] text-[#52525B] px-2 py-1">{t('admin.coupons.inactive')}</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(c)} className="w-8 h-8 inline-flex items-center justify-center text-[#52525B] hover:text-[#0F1B36] hover:bg-[#F4F4F5]" data-testid={`coupon-edit-${c.id}`}>
                      <PencilSimple size={16} />
                    </button>
                    <button onClick={() => setDeleteTarget(c.id)} className="w-8 h-8 inline-flex items-center justify-center text-[#52525B] hover:text-[#E60000] hover:bg-[#F4F4F5]" data-testid={`coupon-delete-${c.id}`}>
                      <Trash size={16} />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl rounded-none" data-testid="coupon-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? t('admin.coupons.edit') : t('admin.coupons.new')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="code">{t('admin.coupons.code')} *</Label>
                <Input
                  id="code"
                  value={form.code}
                  onChange={(e) => update('code', e.target.value.toUpperCase().replace(/\s/g, ''))}
                  disabled={!!editingId}
                  className="rounded-none font-mono"
                  placeholder="GROWTHRIQ50"
                  maxLength={50}
                  data-testid="coupon-form-code"
                />
              </div>
              <div>
                <Label htmlFor="description">{t('admin.coupons.description')}</Label>
                <Input
                  id="description"
                  value={form.description}
                  onChange={(e) => update('description', e.target.value)}
                  className="rounded-none"
                  placeholder="growthriq.co müşterileri için"
                  maxLength={255}
                  data-testid="coupon-form-description"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>{t('admin.coupons.discount_type')} *</Label>
                <Select value={form.discount_type} onValueChange={(v) => update('discount_type', v)}>
                  <SelectTrigger className="rounded-none" data-testid="coupon-form-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">{t('admin.coupons.type.percent')}</SelectItem>
                    <SelectItem value="fixed">{t('admin.coupons.type.fixed')}</SelectItem>
                    <SelectItem value="free">{t('admin.coupons.type.free')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="discount_value">{t('admin.coupons.discount_value')}{form.discount_type !== 'free' && ' *'}</Label>
                <Input
                  id="discount_value"
                  type="number"
                  step="0.01"
                  min="0"
                  max={form.discount_type === 'percent' ? 100 : 99999}
                  value={form.discount_value}
                  onChange={(e) => update('discount_value', e.target.value)}
                  disabled={form.discount_type === 'free'}
                  className="rounded-none"
                  data-testid="coupon-form-value"
                />
                <p className="text-xs text-[#52525B] mt-1">
                  {form.discount_type === 'percent' && '0-100 arası (örn: 50 = %50 indirim)'}
                  {form.discount_type === 'fixed' && 'Sabit USD indirim (örn: 25 = $25 indir)'}
                  {form.discount_type === 'free' && 'Paket bedava olur (değer gerekmez)'}
                </p>
              </div>
            </div>

            <div>
              <Label>{t('admin.coupons.applicable_packages')}</Label>
              <p className="text-xs text-[#52525B] mb-2">{t('admin.coupons.packages_help')}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-[#E4E4E7] p-3">
                {packages.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 cursor-pointer text-sm" data-testid={`coupon-pkg-checkbox-${p.id}`}>
                    <Checkbox
                      checked={form.package_ids.includes(p.id)}
                      onCheckedChange={() => togglePackage(p.id)}
                    />
                    <span>{p.name} <span className="text-[#52525B]">(${p.price})</span></span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="max_uses">{t('admin.coupons.max_uses')}</Label>
                <Input
                  id="max_uses"
                  type="number"
                  min="1"
                  value={form.max_uses}
                  onChange={(e) => update('max_uses', e.target.value)}
                  className="rounded-none"
                  placeholder={t('admin.coupons.unlimited')}
                  data-testid="coupon-form-maxuses"
                />
              </div>
              <div>
                <Label htmlFor="expires_at">{t('admin.coupons.expires_at')}</Label>
                <Input
                  id="expires_at"
                  type="datetime-local"
                  value={form.expires_at}
                  onChange={(e) => update('expires_at', e.target.value)}
                  className="rounded-none"
                  data-testid="coupon-form-expires"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="is_active"
                checked={form.is_active}
                onCheckedChange={(v) => update('is_active', !!v)}
                data-testid="coupon-form-active"
              />
              <Label htmlFor="is_active" className="cursor-pointer">{t('admin.coupons.active')}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none">
              {t('admin.coupons.cancel')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#0F1B36] hover:bg-[#0F1B36]/90 text-white rounded-none"
              data-testid="coupon-form-save"
            >
              {saving ? t('common.loading') : t('admin.coupons.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.coupons.delete')}</AlertDialogTitle>
            <AlertDialogDescription>{t('admin.coupons.confirm_delete')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">{t('admin.coupons.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-[#E60000] hover:bg-[#E60000]/90 text-white rounded-none"
              data-testid="coupon-delete-confirm"
            >
              {t('admin.coupons.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CouponsAdminPanel;
